import { valueAt } from "../common/valueAt";
import type { AlgorithmOperation, OperationType } from "@algoquest/schemas";

/**
 * Bubble Sort — framework-independent Algorithm Engine (Master Spec §8,
 * §13, §24, §25). Values are visible from the start (§24) — there is no
 * hidden/reveal step at all, unlike Searching. Gameplay identity per §25
 * is "adjacent compare + swap/keep + pass": the engine never performs a
 * swap on its own ("Do not automatically perform all swaps" — §25); the
 * player must inspect the current adjacent pair, then commit to "swap" or
 * "keep", and the engine is the sole judge of whether that commitment was
 * correct (a wrong one is rejected and counted as a mistake, never
 * silently auto-corrected — matching Binary Search's inspect/choose model
 * from Phase 03).
 *
 * Two-phase interaction per round, mirroring Binary Search's shape:
 * inspect() reveals the current pair as a COMPARE event; decide() commits
 * to "swap" or "keep". The correct choice is never exposed on the public
 * domain state — only whether a submitted choice was right — so the UI
 * cannot hand players the answer. A full left-to-right pass with no
 * swaps at all means the array is sorted (classic early-exit
 * optimization); otherwise the unsorted region shrinks by one from the
 * right after every pass, since the last comparison of a pass always
 * bubbles the largest (or, descending, smallest) remaining value into
 * its final position.
 */
export interface BubbleSortDomainState {
  values: number[];
  sortDirection: "ascending" | "descending";
  pointer: number;
  unsortedBoundary: number;
  awaitingDecision: boolean;
  sortedIndices: number[];
  comparisons: number;
  swaps: number;
  passesCompleted: number;
  mistakes: number;
  isComplete: boolean;
}

export interface BubbleSortStepResult {
  operations: AlgorithmOperation[];
  state: BubbleSortDomainState;
}

export interface BubbleSortDecisionResult {
  wasCorrect: boolean;
  result: BubbleSortStepResult | null;
}

export class BubbleSortEngine {
  private readonly values: number[];
  private readonly sortDirection: "ascending" | "descending";
  private pointer = 0;
  private unsortedBoundary: number;
  private awaitingDecision = false;
  private sortedIndices: number[] = [];
  private comparisons = 0;
  private swaps = 0;
  private passesCompleted = 0;
  private mistakes = 0;
  private isComplete = false;
  private passHadSwap = false;
  private sequenceCounter = 0;
  private readonly trace: AlgorithmOperation[] = [];

  constructor(values: number[], sortDirection: "ascending" | "descending") {
    if (values.length < 2) {
      throw new Error("BubbleSortEngine requires at least 2 elements");
    }
    this.values = [...values];
    this.sortDirection = sortDirection;
    this.unsortedBoundary = values.length;
  }

  getState(): BubbleSortDomainState {
    return {
      values: [...this.values],
      sortDirection: this.sortDirection,
      pointer: this.pointer,
      unsortedBoundary: this.unsortedBoundary,
      awaitingDecision: this.awaitingDecision,
      sortedIndices: [...this.sortedIndices].sort((a, b) => a - b),
      comparisons: this.comparisons,
      swaps: this.swaps,
      passesCompleted: this.passesCompleted,
      mistakes: this.mistakes,
      isComplete: this.isComplete,
    };
  }

  getTrace(): AlgorithmOperation[] {
    return [...this.trace];
  }

  /** Reveals the current adjacent pair as a COMPARE event. Idempotent-safe: null once complete or already awaiting a decision. */
  inspect(nowMs: number): BubbleSortStepResult | null {
    if (this.isComplete || this.awaitingDecision) {
      return null;
    }
    const left = this.pointer;
    const right = this.pointer + 1;
    this.comparisons += 1;
    const operations: AlgorithmOperation[] = [
      this.emit("COMPARE", nowMs, { indices: [left, right], values: [valueAt(this.values, left), valueAt(this.values, right)] }),
    ];
    this.awaitingDecision = true;
    return { operations, state: this.getState() };
  }

  /** The player's "swap" or "keep" commitment for the currently inspected pair. */
  decide(choice: "swap" | "keep", nowMs: number): BubbleSortDecisionResult {
    if (this.isComplete || !this.awaitingDecision) {
      return { wasCorrect: false, result: null };
    }
    const correctChoice: "swap" | "keep" = this.needsSwap() ? "swap" : "keep";
    if (choice !== correctChoice) {
      this.mistakes += 1;
      return { wasCorrect: false, result: null };
    }

    const left = this.pointer;
    const right = this.pointer + 1;
    const operations: AlgorithmOperation[] = [];

    if (choice === "swap") {
      const leftValue = valueAt(this.values, left);
      const rightValue = valueAt(this.values, right);
      this.values[left] = rightValue;
      this.values[right] = leftValue;
      this.swaps += 1;
      this.passHadSwap = true;
      operations.push(this.emit("SWAP", nowMs, { indices: [left, right], values: [rightValue, leftValue] }));
    } else {
      operations.push(this.emit("KEEP", nowMs, { indices: [left, right], values: [valueAt(this.values, left), valueAt(this.values, right)] }));
    }

    this.awaitingDecision = false;
    this.pointer += 1;

    if (this.pointer >= this.unsortedBoundary - 1) {
      this.sortedIndices.push(this.unsortedBoundary - 1);
      this.passesCompleted += 1;
      operations.push(this.emit("PASS_COMPLETE", nowMs, {}));

      const noSwapsThisPass = !this.passHadSwap;
      this.unsortedBoundary -= 1;
      this.pointer = 0;
      this.passHadSwap = false;

      if (this.unsortedBoundary <= 1 || noSwapsThisPass) {
        this.isComplete = true;
        for (let i = 0; i < this.unsortedBoundary; i += 1) {
          if (!this.sortedIndices.includes(i)) {
            this.sortedIndices.push(i);
          }
        }
      }
    }

    return { wasCorrect: true, result: { operations, state: this.getState() } };
  }

  private needsSwap(): boolean {
    const left = valueAt(this.values, this.pointer);
    const right = valueAt(this.values, this.pointer + 1);
    return this.sortDirection === "ascending" ? left > right : left < right;
  }

  private emit(
    type: OperationType,
    timestampMs: number,
    partial: Partial<Omit<AlgorithmOperation, "sequence" | "type" | "timestampMs">>,
  ): AlgorithmOperation {
    const operation: AlgorithmOperation = {
      sequence: this.sequenceCounter,
      type,
      timestampMs,
      ...partial,
    };
    this.sequenceCounter += 1;
    this.trace.push(operation);
    return operation;
  }
}
